/* Shared by the Overview's comparison table and the Add strategy preview, so the two can't drift. */
/**
 * Risk/return stats from a normalized series, computed exactly the way Fyntrak's
 * calculateMetrics does — off the full daily-return series so every metric uses
 * the same basis:
 *  - daily returns r[i] = v[i]/v[i-1] - 1 for every consecutive pair
 *  - Sharpe   = mean/std × √252 (population std, ÷N) over the FULL daily series
 *               (flat days are real no-change days, kept for volatility)
 *  - Win Rate = positive days ÷ ACTIVE days (days with a non-zero move). A sparse
 *               strategy that's flat on non-trade days (e.g. a futures sleeve)
 *               isn't penalized by those flat days — matches Fyntrak's active-day
 *               basis. Dense strategies (equity portfolios) have few flat days, so
 *               this is ~identical to dividing by all days for them.
 *  - Profit Factor = Σ(+returns) / |Σ(−returns)|, capped at 10 when there are
 *    no losing days (matches Fyntrak's no-loss fallback, not ∞)
 *  - Avg Trade Return = mean return over ACTIVE days (per-trade basis, not diluted
 *    by flat no-trade days)
 */
export function computeSeriesRiskStats(series) {
  const rets = [];
  const rows = Array.isArray(series) ? series : [];
  for (let i = 1; i < rows.length; i += 1) {
    const prev = Number(rows[i - 1]?.normalized);
    const cur = Number(rows[i]?.normalized);
    if (Number.isFinite(prev) && Number.isFinite(cur) && prev > 0) {
      rets.push(cur / prev - 1);
    }
  }
  if (!rets.length) {
    return {
      sharpeRatio: null,
      avgDailyReturnPct: null,
      winRatePct: null,
      profitFactor: null,
      dailyStdDevPct: null,
      annualizedStdDevPct: null,
    };
  }
  const mean = rets.reduce((sum, r) => sum + r, 0) / rets.length;
  const variance = rets.reduce((sum, r) => sum + ((r - mean) ** 2), 0) / rets.length;
  const std = Math.sqrt(variance);
  const wins = rets.filter((r) => r > 0).length;
  const losses = rets.filter((r) => r < 0).length;
  // Active days = days with a non-zero move. Win Rate + Avg Trade Return divide by
  // this (not all days) so flat no-trade days don't dilute a sparse strategy.
  const activeCount = wins + losses;
  const activeReturnSum = rets.filter((r) => r !== 0).reduce((sum, r) => sum + r, 0);
  const grossProfit = rets.filter((r) => r > 0).reduce((sum, r) => sum + r, 0);
  const grossLoss = Math.abs(rets.filter((r) => r < 0).reduce((sum, r) => sum + r, 0));
  return {
    sharpeRatio: std > 0 ? (mean / std) * Math.sqrt(252) : null,
    avgDailyReturnPct: activeCount ? (activeReturnSum / activeCount) * 100 : null,
    winRatePct: activeCount ? (wins / activeCount) * 100 : null,
    profitFactor: grossLoss > 0 ? grossProfit / grossLoss : (grossProfit > 0 ? 10 : 0),
    dailyStdDevPct: std * 100,
    annualizedStdDevPct: std * Math.sqrt(252) * 100,
  };
}
